/*
 * Copyright (c) 2022 NEXTWELL INTL SRL. All Rights Reserved.
 * This software is the proprietary information of NEXTWELL INTL SRL.
 * Use is subject to license and non-disclosure terms
 */

import { Component, EventEmitter, Input, NgZone, OnDestroy, OnInit, Output } from "@angular/core";
import { AddressDTO, PointDtoModel } from "@well/address-management-domain-model";
import { Subject } from "rxjs";
import { debounceTime, distinctUntilChanged, takeUntil } from "rxjs/operators";
import { AddressSearchResultMapper } from "../../g-map-result-mapper/address-search-result.mapper";
import { CommonModule } from "@angular/common";
import { TranslateModule } from "@ngx-translate/core";
import { ButtonModule } from "primeng/button";
import { GoogleMapsModule } from "@angular/google-maps";

@Component({
  selector: 'well-map-location-picker',
  templateUrl: './map-location-picker.component.html',
  styleUrls: ['./map-location-picker.component.scss'],
  standalone: true,
  imports: [CommonModule, TranslateModule, ButtonModule, GoogleMapsModule],
})
export class MapLocationPickerComponent implements OnInit, OnDestroy {
  @Input() height = '200px';

  zoom = 8;

  private _location: PointDtoModel | undefined = undefined;
  private center: Subject<google.maps.LatLngLiteral> = new Subject<google.maps.LatLngLiteral>();
  private destroy: Subject<boolean> = new Subject<boolean>();
  centerLatLng: google.maps.LatLng = null;
  error = null;

  showLocationChangedMessage = false;

  get location(): PointDtoModel {
    return this._location;
  }

  @Input() set location(value: PointDtoModel) {
    if (value && value.latitude && value.latitude) {
      this._location = value;
    } else {
      //TODO - Hardcoded default location?
      this._location = {
        longitude: 21.228807879589795,
        latitude: 45.756349059299936,
      };
    }
  }

  @Input() disabled = true;

  private geocoder: google.maps.Geocoder | undefined = undefined;

  private map: google.maps.Map | undefined = undefined;

  onMapReady(event: google.maps.Map) {
    this.geocoder = new google.maps.Geocoder();
    this.map = event;
    this.mapReady.next(this.map);
  }

  @Output() mapReady: EventEmitter<google.maps.Map> = new EventEmitter<google.maps.Map>();

  @Output() locationChanged: EventEmitter<{ pointDTO: PointDtoModel }> = new EventEmitter<{
    pointDTO: PointDtoModel;
  }>();

  @Output() addressChanged: EventEmitter<AddressDTO> = new EventEmitter<AddressDTO>();

  constructor(private zone: NgZone) {}

  ngOnInit() {
    this.center
      .pipe(
        takeUntil(this.destroy),
        debounceTime(200),
        distinctUntilChanged((p, q) => {
          return p.lat === q.lat && p.lng === p.lng;
        })
      )
      .subscribe((event) => {
        const center = new google.maps.LatLng(event.lat, event.lng);
        this.centerLatLng = center;

        const pointDTO = PointDtoModel.asPointDto(center);

        this.locationChanged.next({ pointDTO: pointDTO });
      });
  }

  centerChanged(event: google.maps.LatLng) {
    this.showLocationChangedMessage = true;
    setTimeout(() => {
      this.showLocationChangedMessage = false;
    }, 5000);
    this.center.next(event.toJSON());
  }

  ngOnDestroy(): void {
    this.destroy.next(true);
  }

  click() {
    const geocodeRequest = { location: this.centerLatLng };
    this.geocoder?.geocode(geocodeRequest, this.onGeocodingResponse);
  }

  private onGeocodingResponse = (results: google.maps.GeocoderResult[], status: google.maps.GeocoderStatus) => {
    this.zone.run(() => {
      switch (status) {
        case google.maps.GeocoderStatus.OK:
          this.prepareAndEmitGeocodingResult(results);
          break;
        case google.maps.GeocoderStatus.ZERO_RESULTS:
          this.displayGeocoderError(status);
          break;
        default:
          console.log('Geocoder failed due to: ' + status);
          break;
      }
    });
  };

  private prepareAndEmitGeocodingResult(results: google.maps.GeocoderResult[]) {
    this.error = null;
    const mostRelevantResult = results[0];
    const resultMapper: AddressSearchResultMapper = new AddressSearchResultMapper(mostRelevantResult);
    const result = resultMapper.getAddress();
    this.centerLatLng = null;
    this.addressChanged.next(result);
  }

  private displayGeocoderError(status: google.maps.GeocoderStatus) {
    this.error = status;
  }
}
